import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import Header from '../../component/header/header';
import Sidebar from '../../component/sidebar/sidebar';
import UploadDocumentModal from './components/UploadDocumentModal';
import DocumentDetailPage from './components/DocumentDetailPage';
import { StatusBadge } from './components/ProcessingTracker';
import { useDocuments } from './hooks/useDocSenseData';
import { getDocumentStatus } from './services/docSenseService';
import { useFinancialYears, useSources } from '../../hooks/useApiData';
import './DocSense.css';

const STATUS_OPTIONS = ['PENDING', 'PARSING', 'ANALYZING', 'EXTRACTING', 'COMPLETED', 'FAILED'];

// ─── Searchable Multi-Select ──────────────────────────────────────────────────
const SearchableMultiSelect = ({ options = [], selected = [], onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selectedSet = new Set(selected.map(s => s.value));

  const toggle = (opt) => {
    if (selectedSet.has(opt.value)) {
      onChange(selected.filter(s => s.value !== opt.value));
    } else {
      onChange([...selected, opt]);
    }
  };

  const toggleAll = () => {
    if (selected.length === options.length) onChange([]);
    else onChange([...options]);
  };

  const handleOpen = () => {
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const removeChip = (e, val) => {
    e.stopPropagation();
    onChange(selected.filter(s => s.value !== val));
  };

  const allSelected = selected.length === options.length && options.length > 0;

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 0 }}>
      <div
        onClick={handleOpen}
        style={{
          border: `1.5px solid ${open ? '#3f88a5' : '#e2eaf0'}`,
          borderRadius: 8, minHeight: 36,
          padding: selected.length > 0 ? '4px 32px 4px 8px' : '0 32px 0 12px',
          background: 'white', cursor: 'pointer',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
          boxShadow: open ? '0 0 0 3px rgba(63,136,165,0.1)' : 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          position: 'relative', userSelect: 'none',
        }}
      >
        {selected.length === 0 ? (
          <span style={{ fontSize: '0.875rem', color: '#9ab4bf', lineHeight: '34px' }}>{placeholder}</span>
        ) : selected.length <= 2 ? (
          selected.map(s => (
            <span key={s.value} style={{
              background: 'rgba(63,136,165,0.1)', color: '#3f88a5', borderRadius: 5,
              padding: '2px 6px 2px 8px', fontSize: '0.78rem', fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {s.label}
              <span onClick={(e) => removeChip(e, s.value)} style={{ cursor: 'pointer', opacity: 0.6, fontSize: '0.75rem', lineHeight: 1, fontWeight: 700 }}>×</span>
            </span>
          ))
        ) : (
          <span style={{ background: 'rgba(63,136,165,0.1)', color: '#3f88a5', borderRadius: 5, padding: '3px 10px', fontSize: '0.78rem', fontWeight: 700 }}>
            {selected.length} selected
          </span>
        )}
        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 4 }}>
          {selected.length > 0 && (
            <span onClick={(e) => { e.stopPropagation(); onChange([]); }} style={{ color: '#b0c4ce', fontSize: '0.85rem', cursor: 'pointer', lineHeight: 1, padding: '0 2px' }} title="Clear all">×</span>
          )}
          <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '0.65rem', color: '#b0c4ce' }} />
        </div>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          minWidth: '100%', width: 'max-content', maxWidth: 320,
          zIndex: 600, background: 'white', border: '1.5px solid #e2eaf0',
          borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,0.13)', overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f7' }}>
            <div style={{ position: 'relative' }}>
              <i className="fa fa-search" style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#b0c4ce', fontSize: '0.72rem' }} />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', border: '1.5px solid #e2eaf0', borderRadius: 6,
                  padding: '5px 10px 5px 28px', fontSize: '0.83rem', color: '#1a2e38',
                  outline: 'none', background: '#f7fafc', boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {options.length > 1 && !query && (
            <div
              onClick={toggleAll}
              style={{
                padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 9,
                cursor: 'pointer', borderBottom: '1px solid #f0f4f7',
                background: allSelected ? 'rgba(63,136,165,0.05)' : 'white',
              }}
            >
              <Checkbox checked={allSelected} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#3f88a5' }}>Select All</span>
            </div>
          )}

          <div style={{ maxHeight: 210, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '14px', fontSize: '0.82rem', color: '#9ab4bf', textAlign: 'center' }}>No results</div>
            ) : (
              filtered.map(opt => {
                const isChecked = selectedSet.has(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => toggle(opt)}
                    style={{
                      padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 9,
                      cursor: 'pointer', background: isChecked ? 'rgba(63,136,165,0.05)' : 'transparent',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { if (!isChecked) e.currentTarget.style.background = '#f7fafc'; }}
                    onMouseLeave={(e) => { if (!isChecked) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Checkbox checked={isChecked} />
                    <span style={{ fontSize: '0.855rem', color: '#1a2e38', fontWeight: isChecked ? 600 : 400 }}>{opt.label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Checkbox = ({ checked }) => (
  <div style={{
    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
    border: `2px solid ${checked ? '#3f88a5' : '#c8d8df'}`,
    background: checked ? '#3f88a5' : 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
  }}>
    {checked && <i className="fa fa-check" style={{ color: 'white', fontSize: '0.55rem' }} />}
  </div>
);
// ─────────────────────────────────────────────────────────────────────────────

const DocSense = (props) => {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedSubLocations, setSelectedSubLocations] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState('');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const financialYears = useFinancialYears();
  const financialYearOptions = financialYears.map(fy => ({ label: fy.financial_year_value, value: fy.id }));

  const sources = useSources() || [];

  const locationOptions = useMemo(() =>
    sources.map(s => ({
      label: s.unitCode ? s.unitCode : (s.location.city + ' - ' + s.location.state),
      value: s.id,
    })), [sources]);

  const subLocationOptions = useMemo(() => {
    const parentIds = selectedLocations.map(l => l.value);
    const parents = parentIds.length > 0
      ? sources.filter(s => parentIds.includes(s.id))
      : sources;
    const subs = [];
    parents.forEach(s => (s.subLocation || []).forEach(sub =>
      subs.push({ label: sub.subLocation, value: sub.id })
    ));
    return subs;
  }, [sources, selectedLocations]);

  const locationMap = useMemo(() => {
    const map = {};
    sources.forEach(s => {
      if (s.subLocation.length && s.subLocation.length > 0) {
        s.subLocation.forEach(sub => {
          map[`${s.id}-${sub.id}`] = `${s.unitCode ? s.unitCode : (s.location.city + ' - ' + s.location.state)}-${sub.subLocation}`;
        });
      } else {
        map[s.id] = s.unitCode ? s.unitCode : (s.location.city + ' - ' + s.location.state);
      }
    });
    return map;
  }, [sources]);

  // Reset sub-locations when available options change
  useEffect(() => {
    if (subLocationOptions.length === 0) { setSelectedSubLocations([]); return; }
    const valid = new Set(subLocationOptions.map(s => s.value));
    setSelectedSubLocations(prev => prev.filter(s => valid.has(s.value)));
  }, [subLocationOptions]);

  // API fetch — only triggered when a financialYear is selected
  const fyFilter = useMemo(
    () => (selectedFinancialYear ? { financialYearId: selectedFinancialYear } : null),
    [selectedFinancialYear]
  );
  const { data: allDocuments, setData: setDocuments, isLoading } = useDocuments(fyFilter);

  // Client-side filtering for location, sub-location, status
  const documents = useMemo(() => {
    let list = allDocuments;
    if (selectedLocations.length) {
      const ids = new Set(selectedLocations.map(l => l.value));
      list = list.filter(d => ids.has(d.sourceId));
    }
    if (selectedSubLocations.length) {
      const ids = new Set(selectedSubLocations.map(s => s.value));
      list = list.filter(d => ids.has(d.subLocationId));
    }
    if (selectedStatus) {
      list = list.filter(d => d.status === selectedStatus);
    }
    return list;
  }, [allDocuments, selectedLocations, selectedSubLocations, selectedStatus]);

  useEffect(() => {
    const pollingDocs = allDocuments.filter(d => ['PENDING', 'PARSING', 'ANALYZING', 'EXTRACTING'].includes(d.status));
    if (!pollingDocs.length) return;
    const interval = setInterval(async () => {
      await Promise.all(pollingDocs.map(async (doc) => {
        try {
          const result = await getDocumentStatus(doc.id);
          if (result && result.status !== doc.status)
            setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, status: result.status } : d));
        } catch (e) { /* silent */ }
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, [allDocuments, setDocuments]);

  const handleUploaded = (newDoc) => setDocuments(prev => [newDoc, ...prev]);

  const handleClearFilters = () => {
    setSelectedLocations([]);
    setSelectedSubLocations([]);
    setSelectedStatus('');
    setSelectedFinancialYear('');
  };

  const hasActiveFilters = selectedLocations.length || selectedSubLocations.length || selectedStatus || selectedFinancialYear;

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return '📄';
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['xlsx', 'xls'].includes(ext)) return '📊';
    if (['doc', 'docx'].includes(ext)) return '📝';
    return '📄';
  };

  const getFileName = (doc) =>
    doc?.fileName || doc?.documentProcessing?.rawFileMetadata?.fileName || 'Unknown Document';

  const getFinancialYearLabel = (fyId) =>
    financialYearOptions.find(fy => String(fy.value) === String(fyId))?.label || fyId;

  if (selectedDocumentId) {
    return (
      <div className="d-flex flex-row mainclass" style={{ height: "100vh", overflow: "auto" }}>
        <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, zIndex: 999 }}>
          <Sidebar dataFromParent={props.location.pathname} onSidebarToggle={setSidebarExpanded} />
        </div>
        <div style={{ flex: "1", minWidth: 0, overflowX: "hidden", transition: "all 0.3s ease" }}>
          <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>
          <div className="docsense-content-wrapper">
            {/* Pass financialYearOptions and sources so DocumentDetailPage can resolve names */}
            <DocumentDetailPage
              documentId={selectedDocumentId}
              onBack={() => setSelectedDocumentId(null)}
              financialYearOptions={financialYearOptions}
              sources={sources}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-row mainclass" style={{ height: "100vh", overflow: "auto" }}>
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, zIndex: 999 }}>
        <Sidebar dataFromParent={props.location.pathname} onSidebarToggle={setSidebarExpanded} />
      </div>

      <div style={{ flex: "1", minWidth: 0, overflowX: "hidden", transition: "all 0.3s ease" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>
        <div className="docsense-content-wrapper">

          {/* Page Header */}
          <div className="docsense-page-header">
            <div>
              <h1>DocSense</h1>
              <p>Upload documents to automatically extract ESG KPI data using AI</p>
            </div>
            <button className="docsense-add-btn" onClick={() => setShowUploadModal(true)}>
              <i className="fa fa-plus me-2" />Add Document
            </button>
          </div>

          {/* Filter Bar */}
          <div className="docsense-filter-bar">
            <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={flabel}>Financial Year</span>
                <select
                  value={selectedFinancialYear}
                  onChange={(e) => setSelectedFinancialYear(e.target.value)}
                  className="docsense-filter-select"
                  style={{ height: 36 }}
                >
                  <option value="">All Years</option>
                  {financialYearOptions.map(fy => (
                    <option key={fy.value} value={fy.value}>{fy.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 190, maxWidth: 260 }}>
                <span style={flabel}>Location</span>
                <SearchableMultiSelect
                  options={locationOptions}
                  selected={selectedLocations}
                  onChange={setSelectedLocations}
                  placeholder="All Locations"
                />
              </div>

              {subLocationOptions.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 190, maxWidth: 260 }}>
                  <span style={flabel}>Sub-location</span>
                  <SearchableMultiSelect
                    options={subLocationOptions}
                    selected={selectedSubLocations}
                    onChange={setSelectedSubLocations}
                    placeholder="All Sub-locations"
                  />
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={flabel}>Status</span>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="docsense-filter-select"
                  style={{ height: 36 }}
                >
                  <option value="">All Statuses</option>
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
                  ))}
                </select>
              </div>

              {hasActiveFilters && (
                <div style={{ alignSelf: 'flex-end' }}>
                  <button onClick={handleClearFilters} className="docsense-filter-reset-btn" style={{ height: 36 }}>
                    <i className="fa fa-times me-1" />Clear
                  </button>
                </div>
              )}
            </div>

            <div style={{ fontSize: '0.82rem', color: '#6b8794', whiteSpace: 'nowrap', alignSelf: 'flex-end', paddingBottom: 2 }}>
              {!isLoading && (
                <span>
                  {documents.length !== allDocuments.length
                    ? <>{documents.length} of {allDocuments.length} documents</>
                    : <>{allDocuments.length} document{allDocuments.length !== 1 ? 's' : ''}</>
                  }
                </span>
              )}
            </div>
          </div>

          {/* Document List */}
          {isLoading ? (
            <div className="docsense-loading">
              <Spinner animation="border" style={{ color: '#3f88a5' }} />
              <span>Loading documents...</span>
            </div>
          ) : allDocuments.length === 0 ? (
            <div className="docsense-empty">
              <div className="docsense-empty-icon">🗂️</div>
              <h5>No documents yet</h5>
              <p>Upload your first ESG document to get started</p>
              <button className="docsense-add-btn" onClick={() => setShowUploadModal(true)}>
                <i className="fa fa-plus me-2" />Add Document
              </button>
            </div>
          ) : documents.length === 0 ? (
            <div className="docsense-empty">
              <div className="docsense-empty-icon">🔍</div>
              <h5>No documents match the current filters</h5>
              <p>Try adjusting your filters or <button onClick={handleClearFilters} style={{ background: 'none', border: 'none', color: '#3f88a5', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 'inherit' }}>clear all</button></p>
            </div>
          ) : (
            <div className="docsense-doc-list">
              <div className="docsense-list-header">
                <div style={{ flex: 2 }}>Document</div>
                <div style={{ flex: 1 }}>Financial Year</div>
                <div style={{ flex: 1 }}>Status</div>
                <div style={{ flex: 1 }}>KPIs</div>
                <div style={{ flex: 1 }}>Uploaded On</div>
                <div style={{ width: 80 }}></div>
              </div>

              {documents.map((doc) => {
                const fileName = getFileName(doc);
                const isActive = ['PENDING', 'PARSING', 'ANALYZING', 'EXTRACTING'].includes(doc.status);
                return (
                  <div key={doc.id} className="docsense-doc-row" onClick={() => setSelectedDocumentId(doc.id)}>
                    <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                      <div className="docsense-file-icon">{getFileIcon(fileName)}</div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a2e38', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {fileName}
                        </div>
                        {(doc.subLocationId ? locationMap[`${doc.sourceId}-${doc.subLocationId}`] : locationMap[doc.sourceId]) && (
                          <div style={{ fontSize: '0.75rem', color: '#6b8794', marginTop: 2 }}>
                            <i className="fa fa-map-marker me-1" />
                            {doc.subLocationId ? locationMap[`${doc.sourceId}-${doc.subLocationId}`] : locationMap[doc.sourceId]}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ flex: 1 }}>
                      {doc.financialYearId ? (
                        <span style={{ background: 'rgba(63,136,165,0.08)', color: '#3f88a5', borderRadius: 4, padding: '3px 8px', fontSize: '0.8rem', fontWeight: 500 }}>
                          {getFinancialYearLabel(doc.financialYearId)}
                        </span>
                      ) : <span style={{ color: '#b0c4ce', fontSize: '0.82rem' }}>—</span>}
                    </div>

                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <StatusBadge status={doc.status} />
                      {isActive && <Spinner animation="border" size="sm" style={{ color: '#3f88a5', width: 14, height: 14, borderWidth: 2 }} />}
                    </div>

                    <div style={{ flex: 1 }}>
                      {doc.kpiCount != null ? (
                        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1a2e38' }}>
                          {doc.kpiCount}
                          {doc.savedCount != null && (
                            <span style={{ fontWeight: 400, color: '#27ae60', fontSize: '0.78rem', marginLeft: 6 }}>
                              ({doc.savedCount} saved)
                            </span>
                          )}
                        </span>
                      ) : <span style={{ color: '#b0c4ce', fontSize: '0.82rem' }}>—</span>}
                    </div>

                    <div style={{ flex: 1, fontSize: '0.82rem', color: '#6b8794' }}>{formatDate(doc.createdAt)}</div>

                    <div style={{ width: 80, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      {doc.fileUrl && (
                        <a
                          href={doc.fileUrl}
                          download={getFileName(doc)}
                          onClick={e => e.stopPropagation()}
                          title="Download"
                          style={{ color: '#9ab4bf', fontSize: '0.85rem', lineHeight: 1, padding: 4 }}
                          onMouseEnter={e => e.currentTarget.style.color = '#3f88a5'}
                          onMouseLeave={e => e.currentTarget.style.color = '#9ab4bf'}
                        >
                          <i className="fa fa-download" />
                        </a>
                      )}
                      <span style={{ color: '#3f88a5', fontSize: '0.8rem' }}>
                        <i className="fa fa-chevron-right" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <UploadDocumentModal
          show={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUploaded={handleUploaded}
          financialYearOptions={financialYearOptions}
          sources={sources}
        />
      </div>
    </div>
  );
};

const flabel = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#6b8794',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

export default DocSense;